import { Component } from '@theme/component';

/**
 * <zg-vfe-charts>: interaction for sections/zg-vfe-charts.liquid.
 *
 * The section renders the complete starting state (the starting tab, ZenGUARD™ selected in each chart,
 * methods and the ratings table closed) and container queries choose the scatter or the bar views, so this
 * element only handles input, through Component's declarative on:click / on:keydown attributes:
 * - tabs: click, or ArrowLeft / ArrowRight (wrapping, mirrored in RTL), Home and End on the focused tab
 *   (automatic activation, roving tabindex); each chart panel gets role tabpanel, labelled by its tab, and
 *   the tab row's "about" paragraph follows the selected tab;
 * - points (scatter points, bars, the ZenGUARD™ pill, "Back to ZenGUARD™"): select that point in its chart,
 *   set aria-pressed on the scatter points, show its compare-panel content and announce its name in the
 *   chart's polite live region (only after a click or key press, never on load). "Back" moves focus to the
 *   ZenGUARD™ name, because the link itself disappears;
 * - disclosures (method, all ratings): aria-expanded on the button, hidden on the content;
 * - theme editor: selecting a Rating block selects it in every chart; selecting a block inside a hidden
 *   chart (the chart, a stat, a method or an extreme) switches to that chart; a method block opens its list.
 *   A re-rendered section (shopify:section:load) is a new element, so it starts over in connectedCallback.
 */
class ZgVfeCharts extends Component {
  /** @type {AbortController | null} */
  #abort = null;

  connectedCallback() {
    super.connectedCallback();
    this.#abort = new AbortController();

    for (const tab of this.#tabs()) {
      const panel = this.#panelOf(tab);
      if (!panel) continue;
      panel.setAttribute('role', 'tabpanel');
      panel.setAttribute('aria-labelledby', tab.id);
    }

    document.addEventListener('shopify:block:select', this.#onBlockSelect, { signal: this.#abort.signal });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abort?.abort();
    this.#abort = null;
  }

  /**
   * on:click of a tab.
   * @param {Event} event
   */
  selectTab(event) {
    const tab = event.target;
    if (tab instanceof HTMLElement) this.#activate(tab);
  }

  /**
   * on:keydown of the tab list.
   * @param {KeyboardEvent} event
   */
  tabKeydown(event) {
    if (event.altKey || event.ctrlKey || event.metaKey) return;
    const tabs = this.#tabs();
    const current = tabs.findIndex((tab) => tab === document.activeElement);
    if (current === -1) return;

    const last = tabs.length - 1;
    const rtl = getComputedStyle(this).direction === 'rtl';
    let next = -1;
    switch (event.key) {
      case 'ArrowRight':
        next = rtl ? current - 1 : current + 1;
        break;
      case 'ArrowLeft':
        next = rtl ? current + 1 : current - 1;
        break;
      case 'Home':
        next = 0;
        break;
      case 'End':
        next = last;
        break;
      default:
        return;
    }
    if (next < 0) next = last;
    if (next > last) next = 0;

    event.preventDefault();
    const tab = tabs[next];
    if (tab) this.#activate(tab, { focus: true });
  }

  /**
   * on:click of a point, a bar, the ZenGUARD™ pill or a "Back to ZenGUARD™" link.
   * @param {Event} event
   */
  pick(event) {
    const trigger = event.target;
    if (!(trigger instanceof HTMLElement)) return;
    const chart = trigger.closest('[data-zg-vfe-chart]');
    const key = trigger.dataset.zgVfePick;
    if (!(chart instanceof HTMLElement) || !key) return;

    this.#select(chart, key, { announce: true });

    if (trigger.hasAttribute('data-zg-vfe-back')) {
      const name = chart.querySelector(`[data-zg-vfe-detail="${CSS.escape(key)}"] [data-zg-vfe-name]`);
      if (name instanceof HTMLElement) name.focus();
    }
  }

  /**
   * on:click of a method's caption row (the button in it, or the caption, as in the design).
   * @param {Event} event
   */
  toggleMethod(event) {
    const row = event.target;
    const button = row instanceof HTMLElement ? row.querySelector('button[aria-controls]') : null;
    if (button instanceof HTMLElement) this.#toggle(button);
  }

  /**
   * on:click of "See all ratings".
   * @param {Event} event
   */
  toggleAll(event) {
    const button = event.target;
    if (button instanceof HTMLElement) this.#toggle(button);
  }

  /** @returns {HTMLElement[]} */
  #tabs() {
    return Array.from(this.querySelectorAll('[role="tab"][data-zg-vfe-tab]')).filter(
      (tab) => tab instanceof HTMLElement
    );
  }

  /**
   * @param {Element} tab
   * @returns {HTMLElement | null}
   */
  #panelOf(tab) {
    const id = tab.getAttribute('aria-controls');
    const panel = id ? document.getElementById(id) : null;
    return panel instanceof HTMLElement && this.contains(panel) ? panel : null;
  }

  /**
   * Shows one chart.
   * @param {HTMLElement} tab
   * @param {{ focus?: boolean }} [options]
   */
  #activate(tab, { focus = false } = {}) {
    const tabs = this.#tabs();
    if (!tabs.includes(tab)) return;

    for (const item of tabs) {
      const selected = item === tab;
      item.setAttribute('aria-selected', String(selected));
      item.tabIndex = selected ? 0 : -1;
      const panel = this.#panelOf(item);
      if (panel) panel.hidden = !selected;
      const about = this.querySelector(`[data-zg-vfe-about="${CSS.escape(item.dataset.zgVfeTab ?? '')}"]`);
      if (about instanceof HTMLElement) about.hidden = !selected;
    }

    if (focus) tab.focus();
  }

  /**
   * Selects a point in one chart: its scatter point is pressed and its compare-panel content shown.
   * @param {HTMLElement} chart - A [data-zg-vfe-chart] element
   * @param {string} key - zg, or a Rating block id
   * @param {{ announce?: boolean }} [options] - announce: put the point's name in the live region
   */
  #select(chart, key, { announce = false } = {}) {
    const detail = chart.querySelector(`[data-zg-vfe-detail="${CSS.escape(key)}"]`);
    if (!(detail instanceof HTMLElement)) return;
    const changed = chart.dataset.zgVfeSelected !== key;
    chart.dataset.zgVfeSelected = key;

    for (const point of chart.querySelectorAll('[data-zg-vfe-point]')) {
      if (point instanceof HTMLElement) point.setAttribute('aria-pressed', String(point.dataset.zgVfePoint === key));
    }
    for (const item of chart.querySelectorAll('[data-zg-vfe-detail]')) {
      if (item instanceof HTMLElement) item.hidden = item !== detail;
    }

    if (announce && changed) {
      const live = chart.querySelector('[data-zg-vfe-live]');
      const name = detail.querySelector('[data-zg-vfe-name]');
      if (live && name) live.textContent = name.textContent?.trim() ?? '';
    }
  }

  /**
   * Opens or closes a disclosure button's content.
   * @param {HTMLElement} button
   * @param {boolean} [open] - Force a state; toggles when left out
   */
  #toggle(button, open) {
    const id = button.getAttribute('aria-controls');
    const content = id ? document.getElementById(id) : null;
    if (!(content instanceof HTMLElement)) return;
    const expanded = open ?? button.getAttribute('aria-expanded') !== 'true';
    button.setAttribute('aria-expanded', String(expanded));
    content.hidden = !expanded;
  }

  /** @param {Event} event */
  #onBlockSelect = (event) => {
    const target = event.target;
    if (!(target instanceof HTMLElement) || !this.contains(target)) return;

    // A Rating block: select it in every chart. Stay on the visible chart when it shows the rating too.
    const point = target.closest('[data-zg-vfe-point]');
    if (point instanceof HTMLElement) {
      const key = point.dataset.zgVfePoint ?? '';
      for (const chart of this.querySelectorAll('[data-zg-vfe-chart]')) {
        if (chart instanceof HTMLElement) this.#select(chart, key);
      }
      const visible = this.querySelector('[data-zg-vfe-panel]:not([hidden])');
      if (visible?.querySelector(`[data-zg-vfe-point="${CSS.escape(key)}"]`)) return;
    }

    const panel = target.closest('[data-zg-vfe-panel]');
    if (panel instanceof HTMLElement && panel.hidden) {
      const tab = this.#tabs().find((item) => this.#panelOf(item) === panel);
      if (tab) this.#activate(tab);
    }

    // A method block: open its list so the edited text is visible.
    const list = target.closest('.zg-vfe__method-list');
    if (list instanceof HTMLElement && list.hidden) {
      const button = this.querySelector(`button[aria-controls="${CSS.escape(list.id)}"]`);
      if (button instanceof HTMLElement) this.#toggle(button, true);
    }
  };
}

if (!customElements.get('zg-vfe-charts')) {
  customElements.define('zg-vfe-charts', ZgVfeCharts);
}
